import "./StreamingChat.scss";
import "../../components/Ai/Ai.style.scss";

import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useSelector } from "react-redux";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";

import Icon from "../../components/Icon/Icon";
import InputTextarea from "../../components/InputTextArea/InputTextArea";
import ShimmerLogo from "../../components/ShimmerLogo/ShimmerLogo";
import { sanitizeDashboardData } from "./sanitizeDashboardData";

/**
 * Default message content renderer - renders markdown without any custom actions
 */
const defaultRenderMessageContent = (content) => (
	<ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]}>
		{content}
	</ReactMarkdown>
);

const StreamingChat = ({
	dashboardType = "",
	dashboardData,
	hideInput = false,
	onMessagesChange,
	renderMessageContent = defaultRenderMessageContent,
	apiEndpoint = "/api/v3/dashboard/stream-analyze",
	initialMessage = "Hi there! I'm BlueChip AI, a help dashboard analysis assistant. What can I help you with today?",
	closingMessage = "",
	initialMessageIsHtml = false,
	initialMessageColor,
	assistantName = "BlueChip AI",
}) => {
	const token = useSelector((state) => state.token);
	const [userInput, setUserInput] = useState("");
	const [messages, setMessages] = useState([]);
	const [streamedAnswer, setStreamedAnswer] = useState("");
	const [loading, setLoading] = useState(false);
	const [errorText, setErrorText] = useState("");
	const chatRootRef = useRef(null);

	// Expose message manipulation functions to parent via callback
	useEffect(() => {
		if (onMessagesChange) {
			onMessagesChange({
				addMessage: (message) => {
					setMessages((prev) => [...prev, message]);
				},
				addStreamedMessage: (content) => {
					setStreamedAnswer(content);
				},
				setLoading: (isLoading) => {
					setLoading(isLoading);
				},
				setError: (error) => {
					setErrorText(error);
				},
				clearStreamed: () => {
					setStreamedAnswer("");
				},
				finalizeStreamedMessage: () => {
					if (streamedAnswer) {
						setMessages((prev) => [...prev, { role: "assistant", content: streamedAnswer }]);
						setStreamedAnswer("");
					}
				},
			});
		}
	}, [onMessagesChange, streamedAnswer]);

	const handleSubmit = async (e) => {
		if (e && e.preventDefault) {
			e.preventDefault();
		}

		const trimmedInput = userInput.trim();
		if (!trimmedInput) {
			return;
		}

		const newMessages = [...messages, { role: "user", content: trimmedInput }];
		setMessages(newMessages);
		setUserInput("");
		setStreamedAnswer("");
		setErrorText("");
		setLoading(true);

		let trimmedDashboardData = sanitizeDashboardData(dashboardData);

		trimmedDashboardData = {
			...trimmedDashboardData,
			type: dashboardType,
		};

		try {
			const response = await fetch(`${import.meta.env.VITE_REACT_APP_API_URL}${apiEndpoint}`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					...(token ? { Authorization: `Bearer ${token}` } : {}),
				},
				body: JSON.stringify({
					messages: newMessages,
					dashboardData: trimmedDashboardData,
				}),
			});

			if (!response.ok || !response.body) {
				throw new Error("Unable to connect to the analysis service. Try again later");
			}

			const reader = response.body.getReader();
			const decoder = new TextDecoder("utf-8");

			let buffer = "";
			let assistantText = "";

			while (true) {
				const { value, done } = await reader.read();
				if (done) break;

				buffer += decoder.decode(value, { stream: true });
				const events = buffer.split("\n\n");
				buffer = events.pop() || "";

				for (const event of events) {
					const line = event.trim();
					if (!line.startsWith("data:")) continue;

					const jsonText = line.slice(5).trim();
					if (!jsonText) continue;

					let data;
					try {
						data = JSON.parse(jsonText);
					} catch (err) {
						console.error("Failed to parse SSE chunk:", err);
						continue;
					}

					if (data.content) {
						assistantText += data.content;
						setStreamedAnswer(assistantText);
					}

					if (data.role === "system" && data.content) {
						setErrorText(data.content);
					}
				}
			}

			if (assistantText) {
				setMessages([...newMessages, { role: "assistant", content: assistantText }]);
				setStreamedAnswer("");
			}

			setLoading(false);
		} catch (err) {
			console.error("Streaming error:", err);
			setErrorText(err.message || "An unexpected error occurred.");
			setLoading(false);
		}
	};

	const handleKeyDown = (e) => {
		if (e.target.tagName === "TEXTAREA" && e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSubmit(e);
		}
	};

	useEffect(() => {
		if (!chatRootRef.current) return;

		const container = chatRootRef.current.closest(".card-content.modal-body");
		if (!container) return;

		container.scrollTo({
			top: container.scrollHeight,
			behavior: "smooth",
		});
	}, [messages, streamedAnswer]);

	useEffect(() => {
		if (!chatRootRef.current) return;

		const tables = chatRootRef.current.querySelectorAll("table");

		tables.forEach((table) => {
			const headerCells = Array.from(table.querySelectorAll("thead th"));
			if (!headerCells.length) return;

			const headerTexts = headerCells.map((th) => th.textContent?.trim() || "");

			const bodyRows = table.querySelectorAll("tbody tr");
			bodyRows.forEach((row) => {
				const cells = Array.from(row.querySelectorAll("td"));
				cells.forEach((td, index) => {
					const label = headerTexts[index];
					if (label) td.setAttribute("data-label", label);
				});
			});
		});
	}, [messages, streamedAnswer]);

	return (
		<div className='bluechip-ai' ref={chatRootRef}>
			<div>
				{messages.map((msg, idx) => {
					return (
						<div key={idx} className={`msg ${msg.role === "user" ? "user-msg user" : "system-msg"}`}>
							<strong className='block mb-1'>{msg.role === "user" ? "You" : assistantName}</strong>

							{msg.isHtml ? (
								<div dangerouslySetInnerHTML={{ __html: msg.content }} />
							) : (
								renderMessageContent(msg.content, idx, false)
							)}
						</div>
					);
				})}

				{messages.length !== 0 && closingMessage && (
					<div
						className='system-msg'
						style={initialMessageColor ? { backgroundColor: initialMessageColor } : undefined}
					>
						<strong className='block mb-1'>{assistantName}</strong>

						{initialMessageIsHtml ? (
							<div
								className='text-center'
								dangerouslySetInnerHTML={{
									__html: closingMessage,
								}}
							/>
						) : (
							renderMessageContent(closingMessage, "initial", false)
						)}
					</div>
				)}

				{loading && !streamedAnswer && !errorText && (
					<div className='w-100 ai-loading position-relative d-block'>
						<ShimmerLogo />
					</div>
				)}

				{streamedAnswer && (
					<div className='system-msg'>
						<strong className='llm-name'>{assistantName}</strong>
						{renderMessageContent(streamedAnswer, "streaming", true)}
					</div>
				)}

				{errorText && (
					<div className='error-msg text-white text-center'>
						<strong className='block mb-1'>Error:</strong>
						<span>{errorText}</span>
					</div>
				)}

				{messages.length === 0 && initialMessage && (
					<div
						className='system-msg'
						style={initialMessageColor ? { backgroundColor: initialMessageColor } : undefined}
					>
						<strong className='block mb-1'>{assistantName}</strong>

						{initialMessageIsHtml ? (
							<div
								dangerouslySetInnerHTML={{
									__html: initialMessage,
								}}
							/>
						) : (
							renderMessageContent(initialMessage, "initial", false)
						)}
					</div>
				)}
			</div>

			{!hideInput && (
				<form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
					<InputTextarea
						label='What would like to know about your dashboard?'
						srOnlyLabel
						theme='gray-outline'
						className='flex-1'
						placeholder='What would like to know about your dashboard?'
						value={userInput}
						onValueChange={setUserInput}
					/>
					<button type='submit' disabled={loading} className='btn-sm sm btn btn-primary primary'>
						Send
					</button>
				</form>
			)}
		</div>
	);
};

export default StreamingChat;
